// HRMS-0653: rules and maths for the user's profile photo.
//
// Whatever the user picks is cropped to a square and saved at 512x512, so the
// same file is sharp at every size it is shown: the 34px sidebar tile (68px on
// retina) up to the 160px profile card (320px on retina).

export const PHOTO_RULES = {
  types: ["image/jpeg", "image/png", "image/webp"] as const,
  maxBytes: 5 * 1024 * 1024,
  minPx: 256,
  outputPx: 512,
  quality: 0.9,
  maxZoom: 4,
} as const;

export const PHOTO_RULES_TEXT =
  "JPG, PNG or WebP · up to 5 MB · at least 256×256 px · a square photo with your face centred looks best";

/** Checks done before decoding (type, size). Returns the message to show, or null. */
export function checkPhotoFile(file: { type: string; size: number }): string | null {
  if (!(PHOTO_RULES.types as readonly string[]).includes(file.type)) {
    return "Use a JPG, PNG or WebP image.";
  }
  if (file.size > PHOTO_RULES.maxBytes) return "That image is larger than 5 MB.";
  return null;
}

/** Checks done after decoding (pixel size). */
export function checkPhotoDimensions(width: number, height: number): string | null {
  if (width < PHOTO_RULES.minPx || height < PHOTO_RULES.minPx) {
    return `That image is ${width}×${height} px. Use one at least ${PHOTO_RULES.minPx}×${PHOTO_RULES.minPx} px so it stays sharp.`;
  }
  return null;
}

export interface CropState {
  /** 1 = the image just covers the crop circle; up to PHOTO_RULES.maxZoom. */
  zoom: number;
  /** Image centre's offset from the crop centre, in on-screen px. */
  x: number;
  y: number;
}

/** Scale (screen px per source px) at which the image just covers a `view`-px square. */
export function coverScale(width: number, height: number, view: number): number {
  return view / Math.min(width, height);
}

/** Keeps the image covering the whole crop square at the current zoom. */
export function clampOffset(crop: CropState, width: number, height: number, view: number): CropState {
  const s = coverScale(width, height, view) * crop.zoom;
  const maxX = Math.max(0, (width * s - view) / 2);
  const maxY = Math.max(0, (height * s - view) / 2);
  return {
    zoom: crop.zoom,
    x: Math.min(maxX, Math.max(-maxX, crop.x)),
    y: Math.min(maxY, Math.max(-maxY, crop.y)),
  };
}

/** The square of the source image that the crop shows, in source px. */
export function sourceRect(crop: CropState, width: number, height: number, view: number) {
  const s = coverScale(width, height, view) * crop.zoom;
  const side = view / s;
  const cx = width / 2 - crop.x / s;
  const cy = height / 2 - crop.y / s;
  return { sx: cx - side / 2, sy: cy - side / 2, side };
}

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
  return new Promise(resolve => canvas.toBlob(resolve, type, quality));
}

/** Draws the crop at 512x512. WebP where the browser can encode it, else JPEG. */
export async function renderCrop(img: HTMLImageElement, crop: CropState, view: number): Promise<Blob> {
  const out = PHOTO_RULES.outputPx;
  const canvas = document.createElement("canvas");
  canvas.width = out;
  canvas.height = out;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas is not available in this browser.");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  // PNGs with transparency would otherwise turn black in JPEG.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, out, out);
  const { sx, sy, side } = sourceRect(crop, img.naturalWidth, img.naturalHeight, view);
  ctx.drawImage(img, sx, sy, side, side, 0, 0, out, out);

  const webp = await toBlob(canvas, "image/webp", PHOTO_RULES.quality);
  if (webp && webp.type === "image/webp") return webp;
  const jpeg = await toBlob(canvas, "image/jpeg", PHOTO_RULES.quality);
  if (!jpeg) throw new Error("Could not prepare the photo.");
  return jpeg;
}
